import React from 'react';
import { useTranslation } from 'react-i18next';
import { Popup } from '@/components/UI/Popup/Popup';
import * as S from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.styles';
import { IconBox } from '@/components/UI/IconBox/IconBox.styles';
import { Button } from '@/components/UI/Button/Button';
import { PaymentStatusParams } from '@/bridge/types/payment';
import {
  getPaymentAmount,
  getPaymentEntries,
  getRowName,
  getSuccessModalMessage,
  getSuccessModalTitle,
} from '@/components/Payment/WebViewPaymentStatusModal/WebViewPaymentStatusModal.helpers';
import { PaymentService, PaymentStatus } from '@/api/types/payments.types';
import { usePaymentStatusModalStore } from '@/store/usePaymentStatusModalStore';
import { ButtonAppearance } from '@/components/UI/Button/Button.types';

export const WebViewPaymentStatusModal = () => {
  const { t } = useTranslation();
  const [paymentData, isOpen, close, setIsSuccessOpen] =
    usePaymentStatusModalStore(state => [
      state.webViewPaymentModalData,
      state.isWebViewPaymentModalOpen,
      state.closeWebViewPaymentModal,
      state.setIsSuccessPaymentModalOpen,
    ]);
  const payment = paymentData || ({} as PaymentStatusParams);
  const { status } = payment;
  const serviceId = 'service_id' in payment ? payment.service_id : '';
  const isAddBalance = serviceId === PaymentService.Add_balance_custom;

  const getContent = (): {
    title: string;
    message: string;
    imageUrl: string;
    buttonColor: ButtonAppearance;
    buttonText: string;
    body: React.ReactNode;
  } => {
    switch (status) {
      case PaymentStatus.Success: {
        const amount = getPaymentAmount(payment);
        const rows = getPaymentEntries(t, payment).map(([name, value]) => [
          getRowName(t, name, isAddBalance),
          value,
        ]);
        return {
          title: getSuccessModalTitle(t, serviceId),
          message: getSuccessModalMessage(t, serviceId),
          imageUrl: 'check-circle-rounded-halo.svg',
          buttonColor: 'secondaryLight',
          buttonText: t(`common.got_it`),
          body: (
            <>
              <S.Body>
                {rows.map(([name, value]) => (
                  <S.Row key={name}>
                    <S.Label>{name}</S.Label>
                    <S.Value>{value}</S.Value>
                  </S.Row>
                ))}
              </S.Body>
              {amount && (
                <S.Row>
                  <S.Label>{t('webview.payment_receipt.amount')}</S.Label>
                  <S.Value $variant="6">{amount}</S.Value>
                </S.Row>
              )}
            </>
          ),
        };
      }
      case PaymentStatus.Duplicate:
        return {
          title: t('webview.payment_receipt.duplicate_title'),
          message: t('webview.payment_receipt.duplicate_message'),
          imageUrl: 'warn-circle-rounded.svg',
          buttonColor: 'secondaryLight',
          buttonText: t(`common.got_it`),
          body: null,
        };
      default:
        return {
          title: t('common.payment_error'),
          message: t('webview.payment_receipt.error_message'),
          imageUrl: 'cross-circle-rounded-halo.svg',
          buttonColor: 'secondary',
          buttonText: t(`common.try_again`),
          body: null,
        };
    }
  };

  const { title, message, imageUrl, buttonColor, buttonText, body } =
    getContent();

  const onClose = () => {
    close();

    if (
      status === PaymentStatus.Success &&
      !isAddBalance &&
      !serviceId.includes('bundle')
    ) {
      setIsSuccessOpen(true);
    }
  };

  return (
    <Popup
      isOpen={isOpen}
      size="intermediate"
      close={onClose}
      overlayOpacity={0.6}
    >
      <S.Container>
        <IconBox $filename={imageUrl} $width={50} $height={50} />
        <S.Head>
          <S.Title $variant="4">{title}</S.Title>
          <S.Message>{message}</S.Message>
        </S.Head>
        {body}
        <S.Actions>
          <Button appearance={buttonColor} isFullWidth onClick={onClose}>
            {buttonText}
          </Button>
        </S.Actions>
      </S.Container>
    </Popup>
  );
};
